import { Button } from '@tempo-io/sui-button';
import { cn } from '@tempo-io/sui-class-utils';
import {
  DragIndicator,
  KeyboardArrowDown,
  KeyboardArrowRight,
  MoreHoriz,
  UnfoldLess,
  UnfoldMore,
  VisibilityOff,
} from '@tempo-io/sui-icons';
import { Popover, PopoverTrigger } from '@tempo-io/sui-popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '@tempo-io/sui-tooltip';
import { Popover as RadixPopover } from 'radix-ui';
import { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SwimlaneCustomerChip } from '@/components/common/custom-field-value/CustomerFieldDisplay';
import type { HeaderDisplayValue } from '@/lib/api/types/loop-visualization';
import { useFlag } from '@/lib/hooks/feature-flags/useFlag';
import { DEFAULT_BASE_COLOR, getContrastTextColor } from '@/lib/utils/loop-visualization/colors';
import { usePortalContainer } from '../PortalContainerContext';
import { RADIX_POPOVER_CONTENT_BASE } from '../radixBypassStyles';
import { ITEM_LANE_HEIGHT, ITEM_LANE_HEIGHT_COMPACT } from './TimelineItem';
import { PARENT_HEADER_HEIGHT, VERTICAL_ROW_HEADER_WIDTH } from './useTimelineColumnDayWidth';
import { lightenTimelineBgColor } from './utils/timelineColorUtils';

export const HeaderOptionsMenu = memo(
  ({
    isVertical,
    color,
    toggleReorderModal,
    onHideHeader,
  }: {
    isVertical: boolean;
    color: string;
    toggleReorderModal: () => void;
    onHideHeader?: () => void;
  }) => {
    const { t } = useTranslation();
    const [tooltipOpen, setTooltipOpen] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const portalContainer = usePortalContainer();

    const handleOpenChange = (open: boolean) => {
      setMenuOpen(open);
      setTooltipOpen(false);
    };

    return (
      <div
        // z-50 matches the Radix overlay z-index layer; elevated above the header cell (z-0)
        // so the button remains clickable when the header is hovered in fullscreen mode.
        className={cn('absolute z-50', {
          'top-0.5 left-1/2 -translate-x-1/2': isVertical,
          'right-0.5 top-[calc(50%+2px)] -translate-y-1/2': !isVertical,
        })}
      >
        <Tooltip open={tooltipOpen && !menuOpen} onOpenChange={setTooltipOpen}>
          <Popover open={menuOpen} onOpenChange={handleOpenChange}>
            <PopoverTrigger asChild>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  style={{
                    color: getContrastTextColor(color),
                    backgroundColor: lightenTimelineBgColor(color, 30),
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                  className={cn(
                    'opacity-0 group-hover:opacity-100 hover:bg-transition-opacity h-6 w-6',
                    {
                      'opacity-100': menuOpen,
                    },
                  )}
                >
                  <MoreHoriz />
                </Button>
              </TooltipTrigger>
            </PopoverTrigger>
            {/* RadixPopover.Content is used directly (instead of the SUI PopoverContent wrapper)
                so we can supply a custom portal container in fullscreen mode. The SUI wrapper does
                not expose a `container` prop on its Portal. When the view is fullscreen the
                Fullscreen API only shows the fullscreen element's descendants, so portalling to
                document.body would make this menu invisible. Track SUI portal support: TODO add
                ticket link. The class string below mirrors the SUI PopoverContent styles. */}
            <RadixPopover.Portal container={portalContainer ?? undefined}>
              <RadixPopover.Content
                data-slot="popover-content"
                className={cn(RADIX_POPOVER_CONTENT_BASE, 'p-0 w-max flex flex-col')}
                side="bottom"
                align="start"
                sideOffset={0}
              >
                <Button
                  variant="ghost"
                  className="items-start justify-start"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleReorderModal();
                  }}
                >
                  <DragIndicator className="w-6 h-6 text-text-neutral-primary cursor-move" />
                  {t(($) => $.views.swimlane.headerMenu.reorderHeaders)}
                </Button>
                {onHideHeader && (
                  <Button
                    variant="ghost"
                    className="items-start justify-start"
                    onClick={(e) => {
                      e.stopPropagation();
                      onHideHeader();
                      handleOpenChange(false);
                    }}
                  >
                    <VisibilityOff className="w-6 h-6 text-text-neutral-primary" />
                    {t(($) => $.views.swimlane.headerMenu.hideHeader)}
                  </Button>
                )}
              </RadixPopover.Content>
            </RadixPopover.Portal>
          </Popover>

          <TooltipContent
            side={isVertical ? 'right' : 'bottom'}
            align="center"
            sideOffset={6}
            className="text-sm bg-bg-surface-base text-text-neutral-primary border-2"
          >
            {t(($) => $.views.visualization.header.options)}
          </TooltipContent>
        </Tooltip>
      </div>
    );
  },
);

export const HeaderTooltip = memo(
  ({
    label,
    displayValues,
    isVertical,
    isExpanded,
    color,
    alignLeft = false,
    icon,
    leadingIcon,
    trailingIcon,
    chevronSpacer = false,
    onNameClick,
  }: {
    label: string;
    displayValues?: HeaderDisplayValue[];
    isVertical: boolean;
    isExpanded?: boolean;
    color: string;
    alignLeft?: boolean;
    icon?: React.ReactNode;
    leadingIcon?: React.ReactNode;
    trailingIcon?: React.ReactNode;
    chevronSpacer?: boolean;
    onNameClick?: () => void;
  }) => {
    const ideaManagerEnabled = useFlag('ideaManagerEnabled');
    const includeExpansionIcon = isExpanded !== undefined;
    const textColor = getContrastTextColor(color);
    const customerDisplayValues = ideaManagerEnabled
      ? (displayValues?.filter((displayValue) => displayValue.type === 'customer') ?? [])
      : [];
    const content =
      customerDisplayValues.length > 0 ? (
        <span
          className={cn('inline-flex min-w-0 items-center gap-2 overflow-hidden', {
            'flex-col': isVertical,
          })}
        >
          {customerDisplayValues.map((customer) => (
            <SwimlaneCustomerChip
              key={customer.id}
              customer={customer}
              compact
              className="max-w-full"
            />
          ))}
        </span>
      ) : (
        label
      );

    return (
      <Tooltip>
        <TooltipTrigger asChild className="group">
          <div
            className={cn('flex min-w-0 h-full w-full items-center', {
              'flex-col': isVertical,
              'flex-row': !isVertical,
              'justify-center': !alignLeft,
              'justify-start': alignLeft,
            })}
            style={{ color: textColor }}
          >
            {leadingIcon ? <span className="shrink-0 flex items-center">{leadingIcon}</span> : null}
            {chevronSpacer ? <span className="w-6 h-6 shrink-0" aria-hidden /> : null}
            {includeExpansionIcon && isVertical ? (
              isExpanded ? (
                <KeyboardArrowRight className="w-5 h-5 shrink-0" />
              ) : (
                <KeyboardArrowDown className="w-5 h-5 shrink-0" />
              )
            ) : null}
            {icon ? (
              <span className={cn('shrink-0 flex items-center', { 'mr-1': alignLeft })}>
                {icon}
              </span>
            ) : null}
            <div
              className={cn('flex items-center min-w-0 min-h-0 shrink', {
                'overflow-hidden': isVertical,
                'w-full flex-1': !isVertical,
              })}
            >
              {onNameClick ? (
                <button
                  type="button"
                  className={cn(
                    'align-middle font-semibold uppercase text-xs min-w-0 truncate cursor-pointer hover:underline bg-transparent border-0',
                    {
                      'text-center': !alignLeft,
                      'text-left': alignLeft,
                      '[writing-mode:vertical-rl] rotate-180 max-h-full':
                        isVertical && customerDisplayValues.length === 0,
                      'p-1': !includeExpansionIcon,
                    },
                  )}
                  onClick={(e) => {
                    e.stopPropagation();
                    onNameClick();
                  }}
                  data-no-dnd
                >
                  {content}
                </button>
              ) : (
                <span
                  className={cn('align-middle font-semibold uppercase text-xs min-w-0 truncate', {
                    'text-center': !alignLeft,
                    'text-left': alignLeft,
                    '[writing-mode:vertical-rl] rotate-180 max-h-full':
                      isVertical && customerDisplayValues.length === 0,
                    'p-1': !includeExpansionIcon,
                  })}
                >
                  {content}
                </span>
              )}
            </div>
            {includeExpansionIcon && !isVertical ? (
              isExpanded ? (
                <KeyboardArrowDown className="w-5 h-5 shrink-0" />
              ) : (
                <KeyboardArrowRight className="w-5 h-5 shrink-0" />
              )
            ) : null}
            {trailingIcon ? (
              <span className="shrink-0 flex items-center ml-1">{trailingIcon}</span>
            ) : null}
          </div>
        </TooltipTrigger>
        <TooltipContent
          side={isVertical ? 'right' : 'bottom'}
          align="end"
          className="text-sm bg-bg-surface-base text-text-neutral-primary border-2 max-w-60"
        >
          {customerDisplayValues.length > 0 ? (
            <span className="flex flex-col gap-1">
              {customerDisplayValues.map((customer) => (
                <SwimlaneCustomerChip key={customer.id} customer={customer} compact />
              ))}
            </span>
          ) : (
            label
          )}
        </TooltipContent>
      </Tooltip>
    );
  },
);

HeaderTooltip.displayName = 'HeaderTooltip';

export const VerticalParentRowHeader = memo(
  ({
    expanded,
    isCompact = false,
    label = '',
    displayValues,
    color = DEFAULT_BASE_COLOR,
    totalHeight,
    onClick,
    toggleReorderModal,
    onHideHeader,
  }: {
    expanded: boolean;
    isCompact?: boolean;
    label?: string;
    displayValues?: HeaderDisplayValue[];
    color?: string;
    totalHeight: number;
    onClick: () => void;
    toggleReorderModal?: () => void;
    onHideHeader?: () => void;
  }) => {
    return (
      // biome-ignore lint/a11y/noStaticElementInteractions: needs to be interactive
      // biome-ignore lint/a11y/useKeyWithClickEvents: needs to be interactive
      <div
        className={cn(
          'group relative left-0 flex justify-center rounded-xs bg-bg-surface-base cursor-pointer top-0 right-0',
          {
            'rounded-xs': !isCompact,
          },
        )}
        style={{
          width: `${PARENT_HEADER_HEIGHT}px`,
          maxWidth: `${PARENT_HEADER_HEIGHT}px`,
          height: `${totalHeight}px`,
          maxHeight: `${totalHeight}px`,
        }}
        onClick={onClick}
      >
        <div
          className="relative flex w-full h-full justify-center items-center rounded-xs top-0 py-0.5"
          style={{ backgroundColor: color }}
        >
          <HeaderTooltip
            label={label}
            displayValues={displayValues}
            isVertical
            isExpanded={expanded}
            color={color}
          />
          {typeof toggleReorderModal === 'function' && (
            <HeaderOptionsMenu
              color={color}
              toggleReorderModal={toggleReorderModal}
              isVertical
              onHideHeader={onHideHeader}
            />
          )}
        </div>
      </div>
    );
  },
);

VerticalParentRowHeader.displayName = 'VerticalParentRowHeader';

export const ParentRowHeader = memo(
  ({
    expanded,
    label = '',
    displayValues,
    color = DEFAULT_BASE_COLOR,
    hasVerticalSubHeaders,
    onClick,
    toggleReorderModal,
    onHideHeader,
    roundingClassName = 'rounded-lg',
    icon,
    leadingIcon,
    trailingIcon,
    onNameClick,
  }: {
    expanded: boolean;
    label?: string;
    displayValues?: HeaderDisplayValue[];
    color?: string;
    hasVerticalSubHeaders: boolean;
    onClick: () => void;
    toggleReorderModal?: () => void;
    onHideHeader?: () => void;
    roundingClassName?: string;
    icon?: React.ReactNode;
    leadingIcon?: React.ReactNode;
    trailingIcon?: React.ReactNode;
    onNameClick?: () => void;
  }) => {
    const ideaManagerEnabled = useFlag('ideaManagerEnabled');
    const customerDisplayValues = ideaManagerEnabled
      ? (displayValues?.filter((displayValue) => displayValue.type === 'customer') ?? [])
      : [];

    return (
      <div className="h-full w-full bg-bg-surface-base pr-1">
        {/* biome-ignore lint/a11y/noStaticElementInteractions: needs to be interactive */}
        {/* biome-ignore lint/a11y/useKeyWithClickEvents: needs to be interactive */}
        <div
          className={cn(
            'group h-full w-full overflow-hidden relative flex items-center p-1',
            roundingClassName,
          )}
          style={{ backgroundColor: color }}
          onClick={onClick}
        >
          <Tooltip>
            <TooltipTrigger asChild className="flex h-full w-full items-center cursor-pointer">
              <div
                className={cn('flex h-full w-full items-center cursor-pointer', {
                  'flex-col justify-center': hasVerticalSubHeaders,
                })}
                style={{ color: getContrastTextColor(color) }}
              >
                {leadingIcon ? (
                  <span className="shrink-0 flex items-center">{leadingIcon}</span>
                ) : null}
                {expanded ? (
                  <KeyboardArrowDown className="w-6 h-6 shrink-0" />
                ) : (
                  <KeyboardArrowRight className="w-6 h-6 shrink-0" />
                )}
                {icon ? <span className="shrink-0 flex items-center mr-1">{icon}</span> : null}
                <div className="flex h-full items-center min-w-0 flex-1">
                  {onNameClick ? (
                    <button
                      type="button"
                      className={cn(
                        'font-semibold uppercase text-xs truncate cursor-pointer hover:underline bg-transparent border-0',
                        {
                          '[writing-mode:vertical-rl] whitespace-normal! h-full p-1':
                            hasVerticalSubHeaders,
                          'whitespace-nowrap': !hasVerticalSubHeaders,
                        },
                      )}
                      onClick={(e) => {
                        e.stopPropagation();
                        onNameClick();
                      }}
                      data-no-dnd
                    >
                      {customerDisplayValues.length > 0 ? (
                        <span
                          className={cn('inline-flex min-w-0 items-center gap-2 overflow-hidden', {
                            'flex-col': hasVerticalSubHeaders,
                          })}
                        >
                          {customerDisplayValues.map((customer) => (
                            <SwimlaneCustomerChip
                              key={customer.id}
                              customer={customer}
                              compact
                              className="max-w-full"
                            />
                          ))}
                        </span>
                      ) : (
                        label
                      )}
                    </button>
                  ) : (
                    <span
                      className={cn('font-semibold uppercase text-xs truncate', {
                        '[writing-mode:vertical-rl] whitespace-normal! h-full p-1':
                          hasVerticalSubHeaders,
                        'whitespace-nowrap': !hasVerticalSubHeaders,
                      })}
                    >
                      {customerDisplayValues.length > 0 ? (
                        <span
                          className={cn('inline-flex min-w-0 items-center gap-2 overflow-hidden', {
                            'flex-col': hasVerticalSubHeaders,
                          })}
                        >
                          {customerDisplayValues.map((customer) => (
                            <SwimlaneCustomerChip
                              key={customer.id}
                              customer={customer}
                              compact
                              className="max-w-full"
                            />
                          ))}
                        </span>
                      ) : (
                        label
                      )}
                    </span>
                  )}
                </div>
                {trailingIcon ? (
                  <span className="shrink-0 flex items-center ml-1">{trailingIcon}</span>
                ) : null}
              </div>
            </TooltipTrigger>
            <TooltipContent
              side="bottom"
              align="end"
              sideOffset={6}
              className="text-sm bg-bg-surface-base text-text-neutral-primary border-2"
            >
              {customerDisplayValues.length > 0 ? (
                <span className="flex flex-col gap-1">
                  {customerDisplayValues.map((customer) => (
                    <SwimlaneCustomerChip key={customer.id} customer={customer} compact />
                  ))}
                </span>
              ) : (
                label
              )}
            </TooltipContent>
          </Tooltip>
          {typeof toggleReorderModal === 'function' && (
            <HeaderOptionsMenu
              color={color}
              toggleReorderModal={toggleReorderModal}
              isVertical={hasVerticalSubHeaders}
              onHideHeader={onHideHeader}
            />
          )}
        </div>
      </div>
    );
  },
);

ParentRowHeader.displayName = 'ParentRowHeader';

export const RowHeader = memo(
  ({
    isChild = false,
    label,
    displayValues,
    color,
    isVertical,
    isCompact = false,
    toggleReorderModal,
    onHideHeader,
    roundingClassName = 'rounded-lg',
    alignLeft = false,
    icon,
    leadingIcon,
    trailingIcon,
    chevronSpacer = false,
    onNameClick,
  }: {
    isChild?: boolean;
    label: string;
    displayValues?: HeaderDisplayValue[];
    color: string;
    isVertical: boolean;
    isCompact?: boolean;
    toggleReorderModal?: () => void;
    onHideHeader?: () => void;
    roundingClassName?: string;
    alignLeft?: boolean;
    icon?: React.ReactNode;
    leadingIcon?: React.ReactNode;
    trailingIcon?: React.ReactNode;
    chevronSpacer?: boolean;
    onNameClick?: () => void;
  }) => {
    if (isVertical) {
      const buffer = isCompact ? 4 : 8;
      const cellWidth = VERTICAL_ROW_HEADER_WIDTH - buffer;

      return (
        <div
          className={cn(
            'group absolute left-1 flex justify-center min-w-0 bg-bg-surface-base rounded-xs',
            {
              'top-1': !isCompact,
              'top-0': isCompact,
            },
          )}
          style={{
            height: 'calc(100% - 4px)',
            width: `${cellWidth}px`,
            maxWidth: `${cellWidth}px`,
          }}
        >
          <div
            className={cn('relative flex w-full h-full justify-center items-center min-w-0 ', {
              'flex-col': isVertical,
            })}
            style={{
              backgroundColor: color,
            }}
          >
            <HeaderTooltip
              label={label}
              displayValues={displayValues}
              isVertical
              color={color}
              onNameClick={onNameClick}
            />
            {typeof toggleReorderModal === 'function' && (
              <HeaderOptionsMenu
                color={color}
                toggleReorderModal={toggleReorderModal}
                isVertical
                onHideHeader={onHideHeader}
              />
            )}
          </div>
        </div>
      );
    }

    return (
      <div
        className={cn(
          'group max-h-full max-w-full h-full w-full flex overflow-hidden min-w-0 bg-bg-surface-base pr-1',
          roundingClassName,
          {
            'pl-1': !isChild,
          },
        )}
      >
        <div
          className={cn('relative flex w-full h-full items-center px-1', roundingClassName, {
            'justify-center': !alignLeft,
            'justify-start': alignLeft,
          })}
          style={{ backgroundColor: color }}
        >
          <HeaderTooltip
            label={label}
            displayValues={displayValues}
            isVertical={false}
            color={color}
            alignLeft={alignLeft}
            icon={icon}
            leadingIcon={leadingIcon}
            trailingIcon={trailingIcon}
            chevronSpacer={chevronSpacer}
            onNameClick={onNameClick}
          />
          {typeof toggleReorderModal === 'function' && (
            <HeaderOptionsMenu
              color={color}
              toggleReorderModal={toggleReorderModal}
              isVertical={false}
              onHideHeader={onHideHeader}
            />
          )}
        </div>
      </div>
    );
  },
);

RowHeader.displayName = 'RowHeader';

export const ExpandCollapseAllButton = memo(
  ({
    areAllExpanded,
    onToggle,
    disabled = false,
  }: {
    areAllExpanded: boolean;
    onToggle: (event?: unknown) => void;
    disabled?: boolean;
  }) => {
    const { t } = useTranslation();
    return (
      <div className="flex w-full h-full items-center justify-start px-1">
        <Button
          variant="ghost"
          size="sm"
          className="text-xs h-6 rounded-xs text-text-neutral-primary"
          onClick={onToggle}
          disabled={disabled}
        >
          {areAllExpanded ? (
            <>
              <UnfoldLess />
              {t(($) => $.common.collapseAll)}
            </>
          ) : (
            <>
              <UnfoldMore />
              {t(($) => $.common.expandAll)}
            </>
          )}
        </Button>
      </div>
    );
  },
);

ExpandCollapseAllButton.displayName = 'ExpandCollapseAllButton';

export const QuarterHeader = memo(({ quarterLabel }: { quarterLabel: string }) => (
  <div className="p-0.5">
    <div className="border border-border-neutral text-text-neutral bg-bg-neutral rounded-full text-center font-semibold py-1 text-xs truncate overflow-hidden">
      {quarterLabel}
    </div>
  </div>
));

QuarterHeader.displayName = 'QuarterHeader';

export const MonthHeader = memo(
  ({ monthName, isMain }: { monthName: string; isMain?: boolean }) => (
    <div className="relative p-1">
      <div
        className={cn(
          'border border-border-neutral text-center font-semibold rounded-full truncate overflow-hidden text-xs',
          {
            'bg-bg-surface-base rounded-2xl py-1 font-bold text-text-neutral': isMain,
            'py-1 px-1 text-text-neutral-secondary': !isMain,
          },
        )}
      >
        {monthName}
      </div>
    </div>
  ),
);

MonthHeader.displayName = 'MonthHeader';

export const WeekHeader = memo(
  ({
    rangeStart,
    rangeEnd,
    leftOffset,
    width,
  }: {
    rangeStart: string;
    rangeEnd: string;
    leftOffset: number;
    width: number;
  }) => (
    <div className="relative h-full w-full">
      <div className="absolute pr-2 top-0.5" style={{ left: `${leftOffset}px`, width }}>
        <div className="overflow-hidden border border-border-neutral text-text-neutral-secondary bg-bg-surface-base py-1 px-1 text-center text-xs font-semibold rounded-full whitespace-nowrap tracking-tighter">
          {rangeStart} - {rangeEnd}
        </div>
      </div>
    </div>
  ),
);

WeekHeader.displayName = 'WeekHeader';
export const EmptyRowCell = memo(
  ({
    isCompact = false,
    isCollapsed = false,
  }: {
    isCompact?: boolean;
    headerLeftOffset?: number;
    isCollapsed?: boolean;
  }) => {
    return (
      <div
        className={cn('relative w-full', { 'border-b border-bg-subtle': !isCollapsed })}
        style={{
          height: isCollapsed ? 0 : isCompact ? ITEM_LANE_HEIGHT_COMPACT : ITEM_LANE_HEIGHT,
        }}
      />
    );
  },
);

EmptyRowCell.displayName = 'EmptyRowCell';
